import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ChevronDown, ChevronUp } from 'lucide-react';
import SafeHTML from '../components/SafeHTML';
import { getFaqKey } from '../utils/keys';

const RussiaVisaPage = () => {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      question: t('ruFaq1Q'),
      answer: t('ruFaq1A')
    },
    {
      question: t('ruFaq2Q'),
      answer: t('ruFaq2A')
    },
    {
      question: t('ruFaq3Q'),
      answer: t('ruFaq3A')
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <section 
        className="relative h-[500px] bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1513326738677-b964603b136d?w=1920&q=80)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="max-w-2xl">
            <div className="inline-block bg-yellow-400 text-black px-4 py-2 rounded font-bold mb-4">
              {t('ruHeroBadge')}
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('ruHeroTitle')}
            </h1>
            <p className="text-xl text-white/90 mb-8">
              {t('ruHeroDesc')}
            </p>
            <a
              href="https://wa.me/971585862103?text=Hello%2C%20I%20would%20like%20to%20apply%20for%20a%20Russia%20Visa"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-gradient-to-r from-logo-blue to-logo-cyan text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all"
            >
              {t('ruApplyBtn')}
            </a>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        <div className="prose prose-lg max-w-none mb-16">
          <SafeHTML tag="p" className="text-gray-700 text-lg leading-relaxed" html={t('ruIntroP1')} />
          <p className="text-gray-700 text-lg leading-relaxed mt-4">
            {t('ruIntroP2')}
          </p>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">{t('ruRequirementsTitle')}</h2>
          <div className="bg-blue-50 p-6 rounded-lg">
            <p className="text-gray-700 mb-4">{t('ruRequirementsDesc')}</p>
            <ul className="space-y-2 text-gray-700">
              <li>• {t('ruReq1')}</li>
              <li>• {t('ruReq2')}</li>
              <li>• {t('ruReq3')}</li>
              <li>• {t('ruReq4')}</li>
              <li>• {t('ruReq5')}</li>
              <li>• {t('ruReq6')}</li>
            </ul>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">{t('ruBenefitsTitle')}</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 bg-gray-50 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('ruBenefit1Title')}</h3>
              <p className="text-gray-700">{t('ruBenefit1Desc')}</p>
            </div>
            <div className="p-6 bg-gray-50 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('ruBenefit2Title')}</h3>
              <p className="text-gray-700">{t('ruBenefit2Desc')}</p>
            </div>
            <div className="p-6 bg-gray-50 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('ruBenefit3Title')}</h3>
              <p className="text-gray-700">{t('ruBenefit3Desc')}</p>
            </div>
            <div className="p-6 bg-gray-50 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('ruBenefit4Title')}</h3>
              <p className="text-gray-700">{t('ruBenefit4Desc')}</p>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">{t('ruProcessTitle')}</h2>
          
          <div className="space-y-8">
            {[1, 2, 3, 4, 5, 6].map((step) => (
              <div key={step} className="flex gap-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-logo-blue rounded-full flex items-center justify-center text-white text-2xl font-bold">{step}</div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{t(`ruStep${step}Title`)}</h3>
                  <p className="text-gray-600">{t(`ruStep${step}Desc`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-16 bg-gradient-to-r from-logo-blue to-logo-cyan p-8 rounded-lg text-white">
          <h2 className="text-3xl font-bold mb-4">{t('ruCtaTitle')}</h2>
          <p className="text-lg mb-6">
            {t('ruCtaDesc')}
          </p>
          <a
            href="https://wa.me/971585862103?text=I%20want%20to%20apply%20for%20Russia%20Visa"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-white text-logo-blue px-8 py-3 rounded-lg font-semibold hover:shadow-xl transition-all"
          >
            {t('ruCtaBtn')}
          </a>
        </div>

        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">{t('ruFaqTitle')}</h3>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={getFaqKey(faq.question, index)} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-4 text-left bg-white hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-logo-blue flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-logo-blue flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-4 pb-4 bg-gray-50">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

      </div>

      <Footer />
    </div>
  );
};

export default RussiaVisaPage;
